<template>
  <el-dialog
    :model-value="modelValue"
    width="min(720px, calc(100vw - 32px))"
    class="mnt-dialog"
    modal-class="mnt-overlay"
    :title="MAINTENANCE_TITLE"
    :show-close="false"
    align-center
    append-to-body
    destroy-on-close
    @update:model-value="value => !value && emit('dismiss')"
  >
    <template #header="{ titleId, titleClass }">
      <header class="mnt-header">
        <button type="button" class="mnt-close" aria-label="关闭维护公告" @click="emit('dismiss')">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
        </button>
        <div class="mnt-kicker">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v4m0 10v4M3 12h4m10 0h4M5.6 5.6l2.8 2.8m7.2 7.2 2.8 2.8M5.6 18.4l2.8-2.8m7.2-7.2 2.8-2.8M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Zm-3.4 0a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0Z" /></svg>
          <span>BioIntelOS {{ kickerDate }} 维护</span>
        </div>
        <h2 :id="titleId" :class="titleClass">{{ MAINTENANCE_TITLE }}</h2>
        <p>我们将在此时段进行系统维护，期间平台暂时无法访问，给您带来不便敬请谅解。</p>
      </header>
    </template>

    <MaintenanceNoticeContent :payload="payload" />
    <p v-if="ackError" class="mnt-ack-error" role="alert">{{ ackError }}</p>

    <template #footer>
      <footer class="mnt-footer">
        <router-link to="/feedback" class="mnt-link" @click="emit('dismiss')">
          如有疑问，欢迎反馈
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 3h7v7M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></svg>
        </router-link>
        <button type="button" class="mnt-primary" :disabled="acknowledging" @click="emit('acknowledge')">
          {{ acknowledging ? '正在保存…' : '我知道了' }}
        </button>
      </footer>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed } from 'vue'
import MaintenanceNoticeContent from './MaintenanceNoticeContent.vue'
import { MAINTENANCE_TITLE } from '@/utils/maintenanceNotice'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  payload: { type: Object, required: true },
  acknowledging: { type: Boolean, default: false },
  ackError: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'acknowledge', 'dismiss'])

const kickerDate = computed(() => String(props.payload.maintenanceStart || '').slice(0, 10).replace(/-/g, '.'))
</script>

<style>
/* 维护公告弹窗：与 ReleaseAnnouncementDialog 同构（正文区块样式在 MaintenanceNoticeContent 内）。 */
.el-dialog.mnt-dialog { padding: 0; border-radius: 20px; overflow: hidden; color: #17233d; box-shadow: 0 28px 80px rgba(18, 36, 73, .34); max-height: calc(100svh - 40px); display: flex; flex-direction: column; }
.mnt-dialog .el-dialog__header { margin: 0; padding: 0; flex-shrink: 0; }
.mnt-dialog .el-dialog__body { padding: 24px 30px 8px; min-height: 0; overflow: auto; overscroll-behavior: contain; scrollbar-width: thin; }
.mnt-dialog .el-dialog__footer { padding: 0; flex-shrink: 0; }
.mnt-dialog svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
.mnt-overlay { background: rgba(22, 31, 49, .5); backdrop-filter: blur(3px); }
.mnt-header { position: relative; padding: 28px 30px 26px; background: radial-gradient(circle at 92% 5%, rgba(120, 82, 224, .2), transparent 38%), linear-gradient(145deg, #eff4ff, #faf7ff); }
.mnt-close { position: absolute; top: 19px; right: 20px; display: grid; place-items: center; width: 32px; height: 32px; padding: 6px; background: none; border: 0; border-radius: 8px; color: #69778f; cursor: pointer; }
.mnt-close:hover { color: #17233d; background: #17233d09; }
.mnt-kicker { display: flex; align-items: center; gap: 10px; color: #5c3dc1; font-size: 13px; font-weight: 600; padding-right: 30px; }
.mnt-header h2 { font-size: 25px; color: #17233d; line-height: 1.4; letter-spacing: -.4px; margin: 17px 0 9px; font-weight: 500; overflow-wrap: anywhere; }
.mnt-header p { font-size: 14px; line-height: 1.8; color: #69778f; margin: 0; }
.mnt-ack-error { margin: 14px 0 0; color: #b54708; font-size: 13px; line-height: 20px; }
.mnt-footer { padding: 18px 30px 26px; display: flex; justify-content: flex-end; align-items: center; gap: 14px; background: white; }
.mnt-link { display: flex; align-items: center; gap: 6px; font-size: 13px; line-height: 20px; text-decoration: none; color: #69778f; padding: 10px 8px; border-radius: 7px; }
.mnt-link:hover { color: #0840e6; background: #f5f7ff; }
.mnt-link svg { width: 13px; height: 13px; }
.mnt-primary { border: 0; background: #0840e6; color: white; border-radius: 10px; font-size: 14px; font-weight: 600; padding: 12px 19px; line-height: 20px; cursor: pointer; white-space: nowrap; font-family: inherit; }
.mnt-primary:hover { background: #0635c7; }
.mnt-dialog button:focus-visible, .mnt-dialog a:focus-visible { outline: 2px solid #0840e6; outline-offset: 3px; }
@media (max-width: 560px) {
  .mnt-header { padding: 25px 22px 21px; }
  .mnt-kicker { font-size: 11px; gap: 7px; padding-right: 20px; flex-wrap: wrap; }
  .mnt-header h2 { font-size: 22px; margin: 15px 0 8px; }
  .mnt-header p { font-size: 12px; }
  .mnt-close { right: 10px; top: 13px; width: 36px; height: 36px; }
  .mnt-dialog .el-dialog__body { padding: 20px 20px 8px; }
  .mnt-footer { padding: 14px 20px 20px; gap: 9px; }
  .mnt-link { font-size: 12px; padding: 10px 4px; min-height: 44px; box-sizing: border-box; }
  .mnt-primary { font-size: 13px; padding: 12px 16px; }
}
</style>
